import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AdminAuthStatusDto {
  adminAccessAllowed: boolean;
  authenticationRequired: boolean;
  message?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly statusUrl = `${environment.apiUrl}/api/admin/auth/status`;
  private checked = false;
  private readonly accessAllowed$ = new BehaviorSubject<boolean>(false);
  private lastMessage: string | null = null;

  constructor(private readonly http: HttpClient) {}

  /** Observable for nav/shell: true only after a successful admin access check. */
  get adminAccessAllowed$(): Observable<boolean> {
    return this.accessAllowed$.asObservable();
  }

  get accessDeniedMessage(): string | null {
    return this.lastMessage;
  }

  /** Call before entering admin routes. Caches result until denyAccess() or clear(). */
  ensureAdminAccess(): Observable<boolean> {
    if (this.checked) {
      return of(this.accessAllowed$.value);
    }
    return this.http.get<AdminAuthStatusDto>(this.statusUrl).pipe(
      tap((status) => {
        this.checked = true;
        this.lastMessage = status.message ?? null;
        this.accessAllowed$.next(status.adminAccessAllowed);
      }),
      map((status) => status.adminAccessAllowed),
      catchError(() => {
        this.checked = true;
        this.lastMessage = 'Unable to verify admin access.';
        this.accessAllowed$.next(false);
        return of(false);
      })
    );
  }

  denyAccess(message?: string): void {
    this.checked = true;
    if (message) this.lastMessage = message;
    this.accessAllowed$.next(false);
  }

  clear(): void {
    this.checked = false;
    this.lastMessage = null;
    this.accessAllowed$.next(false);
  }
}
